import {
  EMatrixType,
  IGraph,
} from 'affiny-core/modules/api/models/compatibilityReport/compatibilityReport.types';
import { ECompatibilityReportType } from 'affiny-core/modules/api/models/member/member.types';
import { IRootState } from 'affiny-core/modules/state';
import round from 'lodash/round';
import { createSelector } from 'reselect';

/* ***** *****  Utils  ***** ***** */

const SIMILAR_THRESHOLD = 0.2;
const SIMILAR_THRESHOLD_MATRIX = 0.4;
const DIFFERENT_THRESHOLD = 0.5;

const computeDistance = (a = 0, b = 0) => Math.abs(round(a - b, 2));

export const isSimilarValuesGraph = (graph: IGraph) =>
  graph.matrices.every(
    matrix =>
      computeDistance(matrix.viewer.x, matrix.viewee.x) < SIMILAR_THRESHOLD
  );

export const isDifferentValuesGraph = (graph: IGraph) =>
  graph.matrices.every(
    matrix =>
      computeDistance(matrix.viewer.x, matrix.viewee.x) > DIFFERENT_THRESHOLD
  );

export const isOtherValuesGraph = (graph: IGraph) =>
  graph.matrices.every(matrix => {
    const diff = computeDistance(matrix.viewer.x, matrix.viewee.x);

    return diff >= SIMILAR_THRESHOLD && diff <= DIFFERENT_THRESHOLD;
  });

export const isSimilarVisionGraph = (graph: IGraph) =>
  graph.matrices.every(
    matrix =>
      matrix.type === EMatrixType.MATRIX &&
      computeDistance(matrix.viewer.x, matrix.viewee.x) <
        SIMILAR_THRESHOLD_MATRIX &&
      computeDistance(matrix.viewer.y, matrix.viewee.y) <
        SIMILAR_THRESHOLD_MATRIX
  );

export const isDifferentVisionGraph = (graph: IGraph) =>
  graph.matrices.every(
    matrix =>
      matrix.type === EMatrixType.MATRIX &&
      (computeDistance(matrix.viewer.x, matrix.viewee.x) >
        SIMILAR_THRESHOLD_MATRIX ||
        computeDistance(matrix.viewer.y, matrix.viewee.y) >
          SIMILAR_THRESHOLD_MATRIX)
  );

export const isHorizontalGraph = (graph: IGraph) =>
  graph.matrices.every(matrix => matrix.type === EMatrixType.HORIZONTAL);

const selectCompatibilityReport = (
  accountId: string,
  reportType: ECompatibilityReportType
) => (state: IRootState) =>
  state.compatibilityReport.entries[accountId]?.[reportType];

export const selectFilteredGraphs = (
  reportType: ECompatibilityReportType,
  filterMethod: (graph: IGraph) => boolean
) => (accountId = '') =>
  createSelector(selectCompatibilityReport(accountId, reportType), report =>
    report ? report.graphes.filter(filterMethod) : []
  );

/* ***** *****  Graphs selectors  ***** ***** */

export const selectSimilarGraphsFromValuesReport = selectFilteredGraphs(
  ECompatibilityReportType.VALUE,
  isSimilarValuesGraph
);
export const selectDifferentGraphsFromValuesReport = selectFilteredGraphs(
  ECompatibilityReportType.VALUE,
  isDifferentValuesGraph
);
export const selectOtherGraphsFromValuesReport = selectFilteredGraphs(
  ECompatibilityReportType.VALUE,
  isOtherValuesGraph
);
export const selectSimilarGraphsFromVisionReport = selectFilteredGraphs(
  ECompatibilityReportType.VISION,
  isSimilarVisionGraph
);
export const selectDifferentGraphsFromVisionReport = selectFilteredGraphs(
  ECompatibilityReportType.VISION,
  isDifferentVisionGraph
);
export const selectHorizontalGraphsFromVisionReport = selectFilteredGraphs(
  ECompatibilityReportType.VISION,
  isHorizontalGraph
);
export const selectGraphsFromPersonalityReport = selectFilteredGraphs(
  ECompatibilityReportType.PERSONALITY,
  () => true
);

/* ***** *****  Analysis selector  ***** ***** */

export const selectCompatibilityReportAnalyses = (
  accountId = '',
  reportType: ECompatibilityReportType
) =>
  createSelector(
    selectCompatibilityReport(accountId, reportType),
    report => report?.analysis
  );
